import React, {
  useState,
  useContext,
  useDeferredValue,
  useRef,
  useEffect,
  useMemo,
} from "react";

import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import { ThemeContext } from "../context/ThemeContext";

// Added new icons to match the design (Moon, Sun, Plus, File, History, Barcode, ChevronDown)
import {
  FaSearch,
  FaBell,
  FaCalendarAlt,
  FaUserCircle,
  FaMoon,
  FaSun,
  FaPlus,
  FaFileInvoice,
  FaHistory,
  FaBarcode,
  FaChevronDown
} from "react-icons/fa";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useNavigate } from "react-router-dom";

import { ProductContext } from "../context/ProductContext";
import { CustomerContext } from "../context/CustomerContext";

import "../styles/Topbar.css";

function Topbar() {
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);

  const { darkMode, setDarkMode } = useContext(ThemeContext);

  const businessName = localStorage.getItem("businessName") || "Admin User";
  const companyLogo = localStorage.getItem("companyLogo") || "/default-user.png";

  const { products } = useContext(ProductContext);

  const lowStockProducts = useMemo(
    () => products.filter((item) => item.units < 10),
    [products]
  );

  const { customers } = useContext(CustomerContext);

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const searchResults = useMemo(() => {
    const value = deferredSearch.trim().toLowerCase();

    if (!value) return [];

    const productResults = products
      .filter((item) => item.name?.toLowerCase().includes(value))
      .slice(0, 8)
      .map((item) => ({
        type: "Product",
        name: item.name,
      }));

    const customerResults = customers
      .filter((item) => item.name?.toLowerCase().includes(value))
      .slice(0, 8)
      .map((item) => ({
        type: "Customer",
        name: item.name,
      }));

    return [...productResults, ...customerResults];
  }, [customers, deferredSearch, products]);

  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const profileMenuRef = useRef();
  const calendarRef = useRef();
  const notificationRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setShowProfileMenu(false);
      }

      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target)
      ) {
        setShowCalendar(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="topbar">
      
      {/* Search Section */}
      <div className="topbar-search">
        <FaSearch className="search-icon" />
        <div className="global-search-wrapper" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent' }}
          />
          <span className="search-shortcut" style={{ fontSize: '12px', color: '#888', background: '#f1f3f5', padding: '2px 8px', borderRadius: '4px', marginLeft: '10px' }}>
            Ctrl + K
          </span>

          {searchResults.length > 0 && (
            <div className="global-search-dropdown">
              {searchResults.map((item, index) => (
                <div key={index} className="global-search-item">
                  <span>{item.type}</span>
                  <strong>{item.name}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* NEW SECTION: Middle Action Buttons */}
      <div className="topbar-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '0 auto' }}>
        <button className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#0d6efd', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
          <FaPlus /> New Bill (F2)
        </button>
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
          <FaFileInvoice /> Hold Bills
          <span style={{ background: '#0d6efd', color: '#fff', borderRadius: '50%', padding: '2px 6px', fontSize: '11px', marginLeft: '4px' }}>3</span>
        </button>
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
          <FaHistory /> Recent Bills
        </button>
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
          <FaBarcode /> Barcode
        </button>
      </div>

      {/* Right Section */}
      <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        
        {/* 1. Notifications */}
        <div className="notification-wrapper" ref={notificationRef} style={{ position: "relative" }}>
          <div
            className="topbar-icon"
            onClick={(e) => {
              e.stopPropagation();
              setShowNotifications((prev) => !prev);
            }}
            style={{ position: 'relative', cursor: 'pointer' }}
          >
            <FaBell />
            {lowStockProducts.length > 0 && (
              <span className="notification-badge" style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', borderRadius: '50%', fontSize: '10px', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                {lowStockProducts.length}
              </span>
            )}
          </div>

          {showNotifications && (
            <div className="notification-dropdown">
              <h4>Notifications</h4>
              {lowStockProducts.length === 0 ? (
                <p className="empty-notification">No alerts</p>
              ) : (
                lowStockProducts.map((item, index) => (
                  <div key={index} className="notification-item">
                    ⚠️ {item.name} low stock ({item.units} left)
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* 2. Theme Toggle */}
        <div 
          className="topbar-icon theme-toggle" 
          onClick={() => setDarkMode(!darkMode)}
          style={{ cursor: 'pointer' }}
        >
          {darkMode ? <FaSun /> : <FaMoon />}
        </div>

        {/* 3. Calendar (Logic strictly untouched) */}
        <div className="calendar-wrapper" ref={calendarRef}>
          <div
            className="topbar-icon"
            onClick={() => setShowCalendar(!showCalendar)}
            style={{ cursor: 'pointer' }}
          >
            <FaCalendarAlt />
          </div>

          {showCalendar && (
            <div className="calendar-dropdown">
              <h3>Calender</h3>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);

                  // Changed to sessionStorage
                  sessionStorage.removeItem("dashboardFromDate");
                  sessionStorage.removeItem("dashboardToDate");
                  sessionStorage.setItem("dashboardSingleDate", date.toISOString());

                  window.dispatchEvent(new Event("dateFilterChanged"));
                }}
                inline
              />

              <div className="date-fields">
                <h4>Select Date Range</h4>
                <label>From Date</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="date-input"
                />

                <label>To Date</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="date-input"
                />
              </div>

              <button
                className="apply-filter-btn"
                onClick={() => {
                  // Corrected variable names!
                  if (fromDate) sessionStorage.setItem("dashboardFromDate", fromDate);
                  if (toDate) sessionStorage.setItem("dashboardToDate", toDate);
                  if (selectedDate) sessionStorage.setItem("dashboardSingleDate", selectedDate);

                  window.dispatchEvent(new Event("dateFilterChanged"));
                  setShowCalendar(false);
                }}
              >
                Apply Filter
              </button>
            </div>
          )}
        </div>

        {/* 4. Profile Menu */}
        <div className="profile-wrapper" ref={profileMenuRef}>
          <div
            className="profile-box"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', background: '#fff', border: '1px solid #e2e8f0', padding: '4px 12px 4px 6px', borderRadius: '30px' }}
          >
            <img
              src={companyLogo}
              alt="Profile"
              className="topbar-profile-img"
              style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b' }}>{businessName}</span>
              <span style={{ fontSize: '11px', color: '#64748b' }}>Admin</span>
            </div>
            <FaChevronDown style={{ fontSize: '10px', color: '#64748b', marginLeft: '4px' }} />
          </div>

          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="profile-header">
                <img src={companyLogo} alt="Profile" />
                <div>
                  <h4>{businessName}</h4>
                  <p>SYSTEM ADMINISTRATOR</p>
                </div>
              </div>

              <div className="dropdown-item" onClick={() => navigate("/settings")}>
                Profile Settings
              </div>
              <div className="dropdown-item">Manage Branch</div>
              <div className="dropdown-item">Activity Logs</div>
              <div className="dropdown-item">Switch Account</div>
              <div
                className="dropdown-item logout-item"
                onClick={async () => {
                  await signOut(auth);
                  localStorage.removeItem("isLoggedIn");
                  navigate("/");
                }}
              >
                Logout
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default Topbar;
